import { ChevronDown, ChevronUp, ExternalLink, FileText } from 'lucide-react';

const SOURCE_KEY_TEXT_PREVIEW_LENGTH = 80;
const SOURCE_PREVIEW_LENGTH = 140;

function buildSourceKey(source) {
  return [
    source.document_id || source.url || source.title || 'source',
    source.page_number || 'page',
    source.section_title || 'section',
    source.relevance_score || 'score',
    source.text?.slice(0, SOURCE_KEY_TEXT_PREVIEW_LENGTH) || 'text',
  ].join('::');
}

function buildSourceHref(source, isWebSource) {
  if (isWebSource) {
    return source.url;
  }

  const highlight = encodeURIComponent(source.text.substring(0, SOURCE_PREVIEW_LENGTH));
  const pageParam = source.page_number ? `&page=${source.page_number}` : '';
  return `/pdf/${source.document_id}?highlight=${highlight}${pageParam}`;
}

function getSourcesLabel(sourcesCount, isComparison) {
  const sourceLabel = sourcesCount > 1 ? 'Sources' : 'Source';
  const comparisonLabel = isComparison ? ' communes' : '';
  return `${sourcesCount} ${sourceLabel}${comparisonLabel}`;
}

function SourceTypeBadge({ sourceType }) {
  if (sourceType === 'web') {
    return <span className="rounded bg-green-900/30 px-1.5 py-0.5 text-xs text-green-400">WEB</span>;
  }

  return <span className="rounded bg-blue-900/30 px-1.5 py-0.5 text-xs text-blue-400">PDF</span>;
}

function MessageSourceItem({ source, index }) {
  const isWebSource = source.source_type === 'web' && source.url;
  const href = buildSourceHref(source, isWebSource);

  return (
    <div key={buildSourceKey(source)} data-testid={`source-${index}`} className="rounded-sm border border-zinc-800 bg-zinc-900/50 p-4 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-mono text-xs text-zinc-500">Source {index + 1}</span>
        <SourceTypeBadge sourceType={source.source_type} />
        <span className="font-mono text-xs text-zinc-500">{Math.round(source.relevance_score * 100)}%</span>
      </div>
      <div className="mb-1 font-medium text-white">{source.title}</div>
      {source.page_number || source.section_title ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-zinc-500">
          {source.page_number ? <span>Page {source.page_number}</span> : null}
          {source.section_title ? <span>{source.section_title}</span> : null}
        </div>
      ) : null}
      {source.url ? <div className="mb-2 truncate text-xs text-zinc-500">{source.url}</div> : null}
      <p className="mb-3 text-xs leading-relaxed text-zinc-400">{source.text}</p>
      <a
        data-testid={`source-open-link-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-700 hover:text-white"
      >
        {isWebSource ? <ExternalLink className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
        <span>Ouvrir</span>
      </a>
    </div>
  );
}

export const MessageSources = ({ sources, isComparison, showSources, onToggle }) => {
  if (!sources?.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <button
        data-testid="toggle-sources-button"
        onClick={onToggle}
        className="flex items-center gap-2 px-2 text-xs text-zinc-400 transition-colors hover:text-white"
      >
        {showSources ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        <span className="font-mono uppercase tracking-wider">{getSourcesLabel(sources.length, isComparison)}</span>
      </button>

      {showSources ? (
        <div className="space-y-2" data-testid="sources-list">
          {sources.map((source, idx) => (
            <MessageSourceItem key={buildSourceKey(source)} source={source} index={idx} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
